import React, { useMemo } from 'react';
import { RecallReportDetail } from '@/types';
import { useRecallReportStore } from '@/stores';
import { useToastStore } from '@/stores';
import {
  useUpdateRecallReportItems,
  useResetRecallReport,
  useSendRecallReport,
  useDownloadRecallReport,
} from '@/hooks/recall-report';
import { RecallReportTable } from '@/features/recall-report/components';
import { ReportView, VELOCITY_COUNTERPARTY } from './ReportView';
import type { ReportStoreAdapter, ReportMutations } from './ReportView';
import { getErrorMessage } from '@/utils/get-error-message';

interface RecallReportViewProps {
  report: RecallReportDetail | undefined;
  isLoading: boolean;
  error: unknown;
  searchQuery?: string;
}

export const RecallReportView: React.FC<RecallReportViewProps> = ({
  report,
  isLoading,
  error,
  searchQuery,
}) => {
  const addToast = useToastStore((s) => s.addToast);

  // Build store adapter — memoized to preserve referential identity
  const isEditing = useRecallReportStore((s) => s.isEditing);
  const localEdits = useRecallReportStore((s) => s.localEdits);
  const newItems = useRecallReportStore((s) => s.newItems);
  const selectedReportId = useRecallReportStore((s) => s.selectedReportId);
  const enterEditMode = useRecallReportStore((s) => s.enterEditMode);
  const cancelEdit = useRecallReportStore((s) => s.cancelEdit);
  const updateItemQuantity = useRecallReportStore((s) => s.updateItemQuantity);
  const updateItemCounterpartyProductId = useRecallReportStore((s) => s.updateItemCounterpartyProductId);
  const toggleItemDeleted = useRecallReportStore((s) => s.toggleItemDeleted);
  const markItemDeleted = useRecallReportStore((s) => s.markItemDeleted);
  const hasChanges = useRecallReportStore((s) => s.hasChanges);
  const getChangedItems = useRecallReportStore((s) => s.getChangedItems);
  const addNewItem = useRecallReportStore((s) => s.addNewItem);
  const removeNewItem = useRecallReportStore((s) => s.removeNewItem);
  const updateNewItemQuantity = useRecallReportStore((s) => s.updateNewItemQuantity);
  const updateNewItemCounterpartyProductId = useRecallReportStore((s) => s.updateNewItemCounterpartyProductId);
  const getNewItemsPayload = useRecallReportStore((s) => s.getNewItemsPayload);

  const store: ReportStoreAdapter = useMemo(() => ({
    isEditing, localEdits, newItems, selectedReportId,
    enterEditMode, cancelEdit, updateItemQuantity, updateItemCounterpartyProductId, toggleItemDeleted, markItemDeleted,
    hasChanges, getChangedItems, addNewItem, removeNewItem, updateNewItemQuantity, updateNewItemCounterpartyProductId, getNewItemsPayload,
  }), [
    isEditing, localEdits, newItems, selectedReportId,
    enterEditMode, cancelEdit, updateItemQuantity, updateItemCounterpartyProductId, toggleItemDeleted, markItemDeleted,
    hasChanges, getChangedItems, addNewItem, removeNewItem, updateNewItemQuantity, updateNewItemCounterpartyProductId, getNewItemsPayload,
  ]);

  // Mutation hooks
  const updateItemsMutation = useUpdateRecallReportItems({
    onSuccess: () => {
      store.cancelEdit();
      addToast('success', 'Report saved');
    },
    onError: (error) => {
      addToast('error', getErrorMessage(error, 'Failed to save report'));
    },
  });

  const resetMutation = useResetRecallReport({
    onSuccess: () => {
      store.cancelEdit();
      addToast('success', 'Report reset to original');
    },
    onError: () => {
      addToast('error', 'Failed to reset report');
    },
  });

  const sendMutation = useSendRecallReport({
    onSuccess: () => {
      addToast('success', 'Report sent');
    },
    onError: () => {
      addToast('error', 'Failed to send report');
    },
  });

  const { download: downloadCsv } = useDownloadRecallReport();

  const mutations: ReportMutations = {
    updateItems: updateItemsMutation,
    reset: resetMutation,
    send: sendMutation,
    download: downloadCsv,
  };

  return (
    <ReportView
      report={report}
      isLoading={isLoading}
      error={error}
      store={store}
      mutations={mutations}
      TableComponent={RecallReportTable}
      searchQuery={searchQuery}
      resetDialogMessage="Restore report to original FTP state? All changes will be lost."
      sendDialogMessage="Send this recall report to the counterparty now? This action cannot be undone."
      filenamePrefix="VCEX-EXA-568545-RECALL"
      inventoryCounterparty={VELOCITY_COUNTERPARTY}
      counterparty="velocity"
      quantityField="velocity_l_quantity"
    />
  );
};
